import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useState, type FormEvent } from 'react';
import { api, ApiError } from '../api';
import { CATALOG_KEY, useLocations } from './ItemForm';
import { dayLabel } from './whereNow';
import { leaveGuarded, useUnsavedGuard, validForm } from '../ui/unsaved';

export type UnitStatus = 'available' | 'damaged' | 'in_service' | 'missing' | 'retired';
export type UnitPlace = 'default' | 'location' | 'rented';
export type Unit = {
  id: string;
  itemId: string;
  serialNumber: string | null;
  internalCode: string;
  status: UnitStatus;
  notes: string;
  /** Where the unit is: the item's default location, another one, or rented out (remark 3/25). */
  place: UnitPlace;
  locationId: string | null;
  rentedTo: string;
  rentedUntil: string | null;
  version: number;
};
export const STATUS_LABELS: Record<UnitStatus, string> = {
  available: 'Dostępny',
  damaged: 'Uszkodzony',
  in_service: 'W serwisie',
  missing: 'Zaginiony',
  retired: 'Wycofany',
};

function Problem({ error }: { error: unknown }) {
  if (!(error instanceof ApiError)) return null;
  return (
    <p className="alert" role="alert">
      {error.status === 409 ? 'Ktoś już zmienił ten egzemplarz. Odśwież listę i spróbuj ponownie.' : error.message}
    </p>
  );
}

/** "Wypożyczone: Teatr, do 2.11.2026" / "Bus" / the default location's name. */
function placeText(unit: Unit, names: ReadonlyMap<string, string>, home: string): string {
  if (unit.place === 'location') return names.get(unit.locationId ?? '') ?? 'Inna lokalizacja';
  if (unit.place === 'rented') {
    const who = [unit.rentedTo, unit.rentedUntil ? `do ${dayLabel(unit.rentedUntil)}` : ''].filter(Boolean).join(', ');
    return who ? `Wypożyczone: ${who}` : 'Wypożyczone';
  }
  return home;
}

/**
 * The place of one unit: back to the default location at once; another location or a rental opens two small
 * fields and "Zapisz miejsce".
 */
function PlaceEditor({ unit, label, names, onSave, pending }: { unit: Unit; label: string; names: ReadonlyMap<string, string>; onSave: (body: Record<string, unknown>) => Promise<unknown>; pending: boolean }) {
  const ids = useId();
  const [choice, setChoice] = useState<UnitPlace>(unit.place);
  const [locationName, setLocationName] = useState(unit.locationId ? (names.get(unit.locationId) ?? '') : '');
  const [rentedTo, setRentedTo] = useState(unit.rentedTo);
  const [rentedUntil, setRentedUntil] = useState(unit.rentedUntil ?? '');
  const [details, setDetails] = useState(false);
  // the fields show for a new choice; for the current one only after "Zmień"
  const open = choice !== 'default' && (choice !== unit.place || details);
  const changed = choice !== unit.place || (choice === 'rented' && (rentedTo !== unit.rentedTo || rentedUntil !== (unit.rentedUntil ?? ''))) || (choice === 'location' && locationName !== (names.get(unit.locationId ?? '') ?? ''));
  const send = () => {
    const byName = [...names].find(([, n]) => n.toLocaleLowerCase('pl') === locationName.trim().toLocaleLowerCase('pl'))?.[0];
    if (choice === 'location') return onSave({ place: 'location', ...(byName ? { locationId: byName } : { locationName: locationName.trim() }) });
    return onSave({ place: 'rented', rentedTo: rentedTo.trim(), rentedUntil: rentedUntil || null });
  };
  function save(event: FormEvent) {
    event.preventDefault();
    // the error is shown under the row (from the mutation)
    send().catch(() => undefined);
  }
  // owner 2026-10-23 21:14: a new place chosen and not saved: leaving asks first
  const guardRef = useUnsavedGuard({
    dirty: open && changed,
    save: async (el) => {
      if (!validForm(el)) return false;
      await send();
      return true;
    },
  });
  return (
    <div className="unit-place">
      <select
        aria-label={`Miejsce: ${label}`}
        value={choice}
        disabled={pending}
        onChange={(e) => {
          const next = e.target.value as UnitPlace;
          setChoice(next);
          if (next === 'default' && unit.place !== 'default') onSave({ place: 'default' });
        }}
      >
        <option value="default">W domyślnej lokalizacji</option>
        <option value="location">Inna lokalizacja…</option>
        <option value="rented">Wypożyczone…</option>
      </select>
      {choice !== 'default' && choice === unit.place && !details && (
        <button className="btn ghost small" type="button" aria-label={`Zmień miejsce: ${label}`} onClick={() => setDetails(true)}>
          Zmień
        </button>
      )}
      {open && (
        <form ref={guardRef} className="unit-place-form" aria-label={`Miejsce egzemplarza: ${label}`} onSubmit={save}>
          {choice === 'location' ? (
            <label className="field w-md">
              <span>Lokalizacja</span>
              <input value={locationName} onChange={(e) => setLocationName(e.target.value)} list={`${ids}-loc`} required autoComplete="off" />
              <datalist id={`${ids}-loc`}>
                {[...names.values()].map((n) => (
                  <option key={n} value={n} />
                ))}
              </datalist>
            </label>
          ) : (
            <>
              <label className="field w-md">
                <span>Komu (opcjonalnie)</span>
                <input value={rentedTo} onChange={(e) => setRentedTo(e.target.value)} autoComplete="off" />
              </label>
              <label className="field w-sm">
                <span>Do kiedy (opcjonalnie)</span>
                <input type="date" value={rentedUntil} onChange={(e) => setRentedUntil(e.target.value)} />
              </label>
            </>
          )}
          {changed && (
            <button className="btn small" type="submit" disabled={pending}>
              Zapisz miejsce
            </button>
          )}
        </form>
      )}
    </div>
  );
}

/**
 * Owner 2026-10-22: a unit stays editable after it is added — serial number, internal code and notes in a small form
 * under its row (state and place are changed in the row itself). Only what changed is sent, with the version.
 */
function UnitEditForm({ unit, label, onClose }: { unit: Unit; label: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [serial, setSerial] = useState(unit.serialNumber ?? '');
  const [code, setCode] = useState(unit.internalCode);
  const [notes, setNotes] = useState(unit.notes);
  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api(`/api/catalog/units/${unit.id}`, { method: 'PATCH', body: { version: unit.version, ...body } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: CATALOG_KEY });
      onClose();
    },
  });
  const body = (): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    if (serial.trim() !== (unit.serialNumber ?? '')) out.serialNumber = serial.trim();
    if (code.trim() !== unit.internalCode) out.internalCode = code.trim();
    if (notes !== unit.notes) out.notes = notes;
    return out;
  };
  function submit(event: FormEvent) {
    event.preventDefault();
    const b = body();
    if (Object.keys(b).length === 0) onClose();
    else save.mutate(b);
  }
  // owner 2026-10-23 21:14: Escape or leaving with changes asks first
  const guardRef = useUnsavedGuard({
    dirty: Object.keys(body()).length > 0,
    save: async () => {
      await save.mutateAsync(body());
      return true;
    },
  });
  return (
    <form
      ref={guardRef}
      className="unit-edit"
      aria-label={`Egzemplarz: ${label}`}
      onSubmit={submit}
      onKeyDown={(e) => {
        if (e.key !== 'Escape') return;
        e.preventDefault();
        leaveGuarded(onClose, e.currentTarget);
      }}
    >
      <div className="field-row">
        <label className="field w-sm">
          <span>Numer seryjny</span>
          <input value={serial} onChange={(e) => setSerial(e.target.value)} autoComplete="off" autoFocus />
        </label>
        <label className="field w-sm">
          <span>Kod wewnętrzny</span>
          <input value={code} onChange={(e) => setCode(e.target.value)} autoComplete="off" />
        </label>
      </div>
      <label className="field">
        <span>Uwagi</span>
        <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </label>
      <Problem error={save.error} />
      <div className="actions">
        <button className="btn small primary" type="submit" disabled={save.isPending}>
          {save.isPending ? 'Zapisywanie…' : 'Zapisz'}
        </button>
        <button className="btn small ghost" type="button" onClick={onClose}>
          Anuluj
        </button>
      </div>
    </form>
  );
}

function UnitRow({ unit, canEdit, canPlace, names, home }: { unit: Unit; canEdit: boolean; canPlace: boolean; names: ReadonlyMap<string, string>; home: string }) {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: CATALOG_KEY });
  const setStatus = useMutation({
    mutationFn: (status: UnitStatus) => api(`/api/catalog/units/${unit.id}`, { method: 'PATCH', body: { version: unit.version, status } }),
    onSettled: refresh,
  });
  const setPlace = useMutation({
    mutationFn: (body: Record<string, unknown>) => api(`/api/catalog/units/${unit.id}`, { method: 'PATCH', body: { version: unit.version, ...body } }),
    onSettled: refresh,
  });
  const remove = useMutation({
    mutationFn: () => api(`/api/catalog/units/${unit.id}?version=${unit.version}`, { method: 'DELETE' }),
    onSettled: refresh,
  });
  const [editing, setEditing] = useState(false);
  const label = unit.serialNumber ?? (unit.internalCode || 'bez numeru');
  return (
    <li className={`row${editing ? ' unit-editing' : ''}`}>
      <div className="row-main">
        <strong>{label}</strong>
        {unit.serialNumber && unit.internalCode && <div className="muted">Kod: {unit.internalCode}</div>}
        {unit.notes && !editing && <div className="muted unit-notes">{unit.notes}</div>}
        {canPlace && <div className={`muted unit-where${unit.place === 'rented' ? ' rented' : ''}`}>{placeText(unit, names, home)}</div>}
        <Problem error={setStatus.error ?? setPlace.error ?? remove.error} />
      </div>
      {canEdit ? (
        <div className="actions row-actions">
          {canPlace && <PlaceEditor key={`${unit.id}-${unit.version}`} unit={unit} label={label} names={names} onSave={(body) => setPlace.mutateAsync(body)} pending={setPlace.isPending} />}
          <select aria-label={`Stan: ${label}`} value={unit.status} disabled={setStatus.isPending} onChange={(e) => setStatus.mutate(e.target.value as UnitStatus)}>
            {(Object.keys(STATUS_LABELS) as UnitStatus[]).map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s]}
              </option>
            ))}
          </select>
          {!editing && (
            <button className="btn ghost small" type="button" data-feature-id="catalog.unit_edit" aria-label={`Edytuj egzemplarz: ${label}`} onClick={() => setEditing(true)}>
              Edytuj
            </button>
          )}
          <button className="btn small" type="button" aria-label={`Usuń egzemplarz: ${label}`} disabled={remove.isPending} onClick={() => remove.mutate()}>
            Usuń
          </button>
        </div>
      ) : (
        <span className="chip">{STATUS_LABELS[unit.status]}</span>
      )}
      {canEdit && editing && <UnitEditForm key={unit.version} unit={unit} label={label} onClose={() => setEditing(false)} />}
    </li>
  );
}

/** Units (single pieces with their own serial numbers) of a serialized item. */
/** `canPlace`: the role sees "Gdzie teraz" (field `placements`); otherwise the place of a unit is neither shown nor set. */
export function UnitsTable({ itemId, units, canEdit, canPlace = true, defaultLocationName = 'Domyślna lokalizacja' }: { itemId: string; units: Unit[]; canEdit: boolean; canPlace?: boolean; defaultLocationName?: string }) {
  const queryClient = useQueryClient();
  const names = new Map((useLocations().data?.locations ?? []).map((l) => [l.id, l.name]));
  const [serial, setSerial] = useState('');
  const [code, setCode] = useState('');
  const add = useMutation({
    mutationFn: () =>
      api(`/api/catalog/items/${itemId}/units`, {
        method: 'POST',
        body: { ...(serial.trim() ? { serialNumber: serial.trim() } : {}), ...(code.trim() ? { internalCode: code.trim() } : {}) },
      }),
    onSuccess: async () => {
      setSerial('');
      setCode('');
      await queryClient.invalidateQueries({ queryKey: CATALOG_KEY });
    },
  });
  function submit(event: FormEvent) {
    event.preventDefault();
    add.mutate();
  }
  const addRef = useUnsavedGuard({
    dirty: canEdit && (serial.trim() !== '' || code.trim() !== ''),
    save: async () => {
      await add.mutateAsync();
      return true;
    },
  });
  return (
    <section aria-label="Egzemplarze" className="dsec" data-feature-id="catalog.units">
      <h3>Egzemplarze ({units.length})</h3>
      {units.length === 0 && <p className="muted">Brak egzemplarzy.</p>}
      <ul className="list">
        {units.map((u) => (
          <UnitRow key={u.id} unit={u} canEdit={canEdit} canPlace={canPlace} names={names} home={defaultLocationName} />
        ))}
      </ul>
      {canEdit && (
        <form ref={addRef} className="inline-form" aria-label="Nowy egzemplarz" onSubmit={submit}>
          <Problem error={add.error} />
          <label className="field w-sm">
            <span>Numer seryjny</span>
            <input value={serial} onChange={(e) => setSerial(e.target.value)} autoComplete="off" />
          </label>
          <label className="field w-sm">
            <span>Kod wewnętrzny</span>
            <input value={code} onChange={(e) => setCode(e.target.value)} autoComplete="off" />
          </label>
          <button className="btn" type="submit" disabled={add.isPending}>
            Dodaj egzemplarz
          </button>
        </form>
      )}
    </section>
  );
}
